<script setup lang="ts">
import type { Product } from 'types/models/product'
import type { CategoryContent } from '~/types/storyblok/category-page'
import { normalizePlpHero } from '~/types/storyblok/hero-plp'

interface Props {
  blok: CategoryContent
  loading: boolean
  fetchingProducts: boolean
  products?: Product[]
  parentCategory: string
  page: number
  pages: number
  isEmpty: boolean
  prevPageUrl: string | null
  nextPageUrl: string | null
  totalProducts: number
}

const props = defineProps<Props>()

const blok = toRef(props, 'blok')
const filterStore = useFilterStore()

const { filterBarHidden } = storeToRefs(filterStore)

const products = computed(() => {
  return props.products || []
})
const hero = computed(() =>
  blok.value?.hero?.[0] ? normalizePlpHero(blok.value.hero[0]) : undefined,
)

const heroDescription = computed(() => hero.value?.body || blok.value?.description)
const productListMediaCards = computed(() => blok.value.product_list_media_cards)
const quickFiltersKey = computed(() => blok.value.quick_filters?.content?.key || 'pattern')
</script>

<template>
  <div
    v-editable="blok"
    class="collection-page"
  >
    <CollectionHero
      :background-color="hero?.backgroundColor"
      :font-color="hero?.fontColor"
      :edge-type="hero?.edge"
      :heading="blok.name"
      :text="heroDescription"
      :additional-text="hero?.additionalText"
      :media="hero?.image"
      :image-alt-text="hero?.imageAltText"
      :link="{ label: parentCategory }"
    />

    <FilterBar
      v-if="!filterBarHidden"
      :quick-filters-key="quickFiltersKey"
      :hide-quick-filters="blok.hide_quick_filters"
    />
    <main class="product-section">
      <ProductGrid
        :products="products"
        :loading="loading"
        :page="page"
        :pages="pages"
        :is-empty="isEmpty"
        :prev-page-url="prevPageUrl"
        :next-page-url="nextPageUrl"
        :total-products="totalProducts"
        :product-list-media-cards="productListMediaCards"
        :fetching-products="fetchingProducts"
      />
    </main>
  </div>
</template>

<style lang="scss" scoped>
.collection-container:first-of-type {
  margin-top: -0.2rem;
}

.collection-container:last-of-type {
  margin-bottom: 16rem;
}
</style>
